import React from 'react'

import logo from '@/assets/logo.png'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '@/components/ui/breadcrumb'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { useAuth } from '@/contexts/AuthContext'
import { useTheme } from '@/contexts/ThemeContext'
import { ArrowLeftIcon, ArrowRightIcon, CheckCircleIcon, CircleCheck, ForwardIcon, HandCoinsIcon, HandIcon, KeyboardIcon, LoaderCircle, LoaderCircleIcon, LogOutIcon, MegaphoneIcon, MoonIcon, SparklesIcon, SunIcon, SunMoonIcon } from 'lucide-react'
import { Link, Navigate, useNavigate } from 'react-router-dom'
import { useOrder } from '../../contexts/OrderContext'
import { supabase } from '@/lib/supabase'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'
import { useUserActivityTracker } from '@/hooks/activity'
import ShortcutsDialog from './ShortcutsDialog'
import { Separator } from '@/components/ui/separator'
import clsx from 'clsx'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Textarea } from '@/components/ui/textarea'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
const { APP_API_URL } = import.meta.env

export default function OrderHeader({ title, order }) {
    const { user: { settings: { isAdmin } }, toggleBot, showBot } = useAuth()

    return <header className="flex h-16 shrink-0 items-center gap-2 border-b px-4">
        <Breadcrumb>
            <BreadcrumbList>
                <BreadcrumbItem>
                    <BreadcrumbLink className="flex items-center gap-2" to={`/orders`}>
                        <img src={logo} className="h-8 w-8" />
                        <div className='flex flex-col gap-1'>
                            <span style={{
                                fontSize: 16,
                                fontOpticalSizing: "auto",
                                fontWeight: 600,
                                fontStyle: "normal",
                                lineHeight: 1
                            }}>
                                Universus
                            </span>
                            <span style={{
                                fontSize: 10,
                                fontFamily: '"Raleway", sans-serif',
                                fontOpticalSizing: "auto",
                                fontWeight: 400,
                                fontStyle: "normal",
                                lineHeight: 1
                            }}>⟟₪ Đℝ∑⟑Mš ₩€ ₸ℝ⊔$⟘</span>
                        </div>
                    </BreadcrumbLink>
                </BreadcrumbItem>

                {!order && <>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                        <BreadcrumbPage>
                            <div className='flex flex-col gap-1'>
                                <span style={{ lineHeight: 1 }}>{title}</span>
                            </div>
                        </BreadcrumbPage>
                    </BreadcrumbItem>
                </>}

                {!!order && <>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                        <BreadcrumbPage>
                            <div className='flex flex-col gap-1'>
                                <span style={{ lineHeight: 1 }}>{order.company.name}</span>
                            </div>
                        </BreadcrumbPage>
                    </BreadcrumbItem>

                    {<>
                        <BreadcrumbSeparator />
                        <BreadcrumbItem>
                            <BreadcrumbPage>
                                <div className='flex flex-col gap-1'>
                                    <span style={{ lineHeight: 1 }}>{order.doctor.profile.gender === 'M' ? 'Dr' : 'Dre'} {order.doctor.profile.last_name}</span>
                                    {/* {page === 'mandate' && <span style={{ lineHeight: 1 }} className="text-xs">Mandat</span>}
                                {page === 'transcription' && <span style={{ lineHeight: 1 }} className="text-xs">Transcription</span>}
                                {page === 'report' && <span style={{ lineHeight: 1 }} className="text-xs">Rapport</span>}
                                {page === 'complete' && <span style={{ lineHeight: 1 }} className="text-xs">Complété</span>} */}
                                </div>
                            </BreadcrumbPage>
                        </BreadcrumbItem>
                    </>}

                    {<>
                        <BreadcrumbSeparator />
                        <BreadcrumbItem>
                            <BreadcrumbPage>
                                <div className='flex flex-col gap-1'>
                                    <span style={{ lineHeight: 1 }}>{order.pydio_id}</span>
                                </div>
                            </BreadcrumbPage>
                        </BreadcrumbItem>
                    </>}
                </>}
            </BreadcrumbList>
        </Breadcrumb>

        <div className="ml-auto flex items-center gap-2">
            <Tooltip>
                <TooltipTrigger asChild>
                    <Button variant={'ghost'} size={"icon"} onClick={() => toggleBot()}>
                        <SparklesIcon className={clsx("h-8 w-8", "text-primary")} style={{ transform: 'scale(1.3)' }} />
                    </Button>
                </TooltipTrigger>

                <TooltipContent side="left">
                    <Badge style={{
                        position: "absolute",
                        top: "3px",
                        right: "78px",
                        display: "flex",
                        width: "71px"
                    }}>CTRL + K</Badge>
                    <p>Assistant</p>
                </TooltipContent>
            </Tooltip>

            <Menu />

            <Feedback />
        </div>
    </header>
}

function Menu() {
    const { signOut, user } = useAuth()
    const { setTheme, theme } = useTheme()
    const navigate = useNavigate()

    const [isShortcutsOpen, setIsShortcutsOpen] = React.useState(false)

    return <>
        <DropdownMenu modal={false}>
            <DropdownMenuTrigger>
                <AvatarUser />
            </DropdownMenuTrigger>
            <DropdownMenuContent
                className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
                side="bottom"
                align="end"
                sideOffset={4}
            >
                <DropdownMenuLabel className="p-0 font-normal">
                    <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                        <AvatarUser />

                        <div className="grid flex-1 text-left text-sm leading-tight">
                            <span className="truncate font-semibold">
                                {user.profile.first_name} {user.profile.last_name}
                            </span>
                            <span className="truncate text-xs">
                                {user.profile.email}
                            </span>
                        </div>
                    </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/payments')}>
                    <HandCoinsIcon />
                    Paiements
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setIsShortcutsOpen(!isShortcutsOpen)}>
                    <KeyboardIcon />
                    Raccourcis
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}>
                    {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
                    Thème {theme === 'dark' ? 'claire' : 'sombre'}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={signOut}>
                    <LogOutIcon />
                    Déconnexion
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>

        <ShortcutsDialog open={isShortcutsOpen} setOpen={setIsShortcutsOpen} />
    </>
}

function AvatarUser() {
    const { user } = useAuth()

    return <Avatar className="h-8 w-8 rounded-lg">
        <AvatarImage
            src={user.profile.avatar}
            alt={user.profile.first_name}
        />
        <AvatarFallback className="rounded-lg">
            {user.profile.first_name.charAt(0)}
        </AvatarFallback>
    </Avatar>
}

function Feedback() {
    const { user, session } = useAuth()
    const { order } = useOrder()
    const [message, setMessage] = React.useState('')
    const [sent, setSent] = React.useState(false)
    const [loading, setLoading] = React.useState(false)

    const send = async () => {
        setLoading(true)

        const { error } = await fetch(`${APP_API_URL}/ticket`, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({
                message,
                pydio_id: order?.pydio_id || null,
                transcriber_id: user.transcriber.id
            }),
        }).then(res => res.json())

        setLoading(false)

        if (error) {
            alert(error.message)
        } else {
            setSent(true)
        }
    }

    return <>
        <Popover modal={false}>
            <PopoverTrigger>
                <Button variant={'outline'} size={"sm"}>
                    <HandIcon />
                    Support
                </Button>
            </PopoverTrigger>
            <PopoverContent
                className="w-[--radix-dropdown-menu-trigger-width] rounded-lg flex flex-col gap-2 items-end"
                side="bottom"
                style={{ width: 384, height: 210 }}
                align="end"
                sideOffset={4}
            >
                {!sent && <>
                    <Textarea placeholder="Décrivez votre problème" value={message} onChange={(e) => setMessage(e.target.value)} className='flex-grow' />
                    <Button onClick={send} size="sm" disabled={loading || !message.trim().length}>
                        {loading && <LoaderCircleIcon className="animate-spin" />}
                        Envoyer
                    </Button>
                </>}

                {sent && <div className="flex flex-col gap-2 items-center justify-center w-full h-full">
                    <CheckCircleIcon className="text-green-500" />
                    <span className="text-sm font-semibold text-center">Votre message a bien été transmis à l'équipe technique. <br />Nous reviendrons vers vous rapidement.</span>
                </div>}
            </PopoverContent>
        </Popover>
    </>
}